// Asistente de campaña: Tipo → Diseño → Destinatarios → No enviar → Configuración inicial → Envío.
// Guardado automático opcional (cada pocos segundos si hay cambios).
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Check, Mail, MessageCircle, Search, Send, Users } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Loading } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { EmailEditor } from "./EmailEditor.tsx";
import type { AudienceList, AuditIssue, Campaign, EmailDesign } from "./types.ts";
import { VARIABLES } from "./types.ts";

const STEPS = ["Tipo de campaña", "Diseño", "Destinatarios", "No enviar", "Configuración inicial", "Envío"] as const;

interface Draft {
  name: string; subject: string; preheader: string; fromName: string; replyTo: string; folder: string;
  design: EmailDesign | null; listIds: string[]; excludeListIds: string[];
}
const toDraft = (c: Campaign): Draft => ({
  name: c.name, subject: c.subject ?? "", preheader: c.preheader ?? "", fromName: c.fromName ?? "", replyTo: c.replyTo ?? "",
  folder: c.folder ?? "", design: c.design, listIds: c.listIds, excludeListIds: c.excludeListIds,
});
const AUTOSAVE_KEY = "tiger.mkt.campaign.autosave";

export function CampaignWizard(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const q = useQuery({ queryKey: ["mkt-campaign", id], queryFn: () => api.get<Campaign>(`/mkt/campaigns/${id}`) });
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [autosave, setAutosave] = useState<boolean>(() => { try { return localStorage.getItem(AUTOSAVE_KEY) !== "0"; } catch { return true; } });
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => { if (q.data && !draft) setDraft(toDraft(q.data)); }, [q.data, draft]);
  useEffect(() => { try { localStorage.setItem(AUTOSAVE_KEY, autosave ? "1" : "0"); } catch { /* sin almacenamiento */ } }, [autosave]);

  const save = useMutation({
    mutationFn: (d: Draft) => api.patch(`/mkt/campaigns/${id}`, {
      name: d.name.trim() || "Campaña sin nombre", subject: d.subject || null, preheader: d.preheader || null,
      fromName: d.fromName || null, replyTo: d.replyTo.trim() || null, folder: d.folder.trim() || null,
      design: d.design, listIds: d.listIds, excludeListIds: d.excludeListIds,
    }),
    onSuccess: () => { setDirty(false); setSavedAt(new Date()); void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); },
  });
  const draftRef = useRef(draft); draftRef.current = draft;
  const doSave = useCallback(async (): Promise<boolean> => {
    if (!draftRef.current) return false;
    try { await save.mutateAsync(draftRef.current); return true; } catch (e) { toast.error((e as Error).message); return false; }
  }, [save]);

  // Guardado automático: 4 s después del último cambio.
  useEffect(() => {
    if (!autosave || !dirty || !isAdmin) return;
    const t = setTimeout(() => { void doSave(); }, 4000);
    return () => clearTimeout(t);
  }, [draft, dirty, autosave, isAdmin, doSave]);

  // Aviso al salir con cambios sin guardar.
  useEffect(() => {
    const h = (e: BeforeUnloadEvent): void => { if (dirty) e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const patch = useCallback((p: Partial<Draft>): void => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); }, []);
  const onDesign = useCallback((design: EmailDesign) => patch({ design }), [patch]);
  const meta = useMemo(() => ({ subject: draft?.subject ?? "", preheader: draft?.preheader ?? "", fromName: draft?.fromName ?? "" }), [draft?.subject, draft?.preheader, draft?.fromName]);

  if (q.isLoading || (q.data && !draft)) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message={q.error?.message ?? "No se pudo cargar la campaña."} onRetry={() => void q.refetch()} />;
  if (q.data.status !== "draft") {
    return <Card className="p-6 space-y-3"><p className="text-sm text-ink-secondary">Esta campaña ya no está en borrador y no se puede editar.</p><Link className="link text-sm" to={`/marketing/campanas/${id}`}>Ver estadísticas</Link></Card>;
  }
  if (!isAdmin) return <Card className="p-6"><p className="text-sm text-ink-muted">Solo los administradores pueden crear o editar campañas.</p></Card>;
  const d = draft!;

  const go = async (n: number): Promise<void> => {
    if (dirty && !(await doSave())) return;
    setStep(Math.max(0, Math.min(STEPS.length - 1, n)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/marketing/campanas" className="btn-ghost btn-sm"><ArrowLeft size={14} className="mr-1 inline" />Campañas</Link>
        <Input className="!w-auto min-w-[240px] font-medium" value={d.name} maxLength={160} onChange={(e) => patch({ name: e.target.value })} aria-label="Nombre de la campaña" />
        <div className="ml-auto flex items-center gap-3 text-xs text-ink-muted">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={autosave} onChange={(e) => setAutosave(e.target.checked)} />Guardado automático</label>
          <span>{save.isPending ? "Guardando…" : dirty ? "Cambios sin guardar" : savedAt ? `Guardado ${savedAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}` : "Guardado"}</span>
          <Button small onClick={() => void doSave()} disabled={save.isPending || !dirty}>Guardar</Button>
        </div>
      </div>

      {/* Pasos */}
      <nav className="flex gap-1 overflow-x-auto scrollbar-thin" aria-label="Pasos">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => void go(i)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-xs border ${i === step ? "border-primary bg-primary-soft text-primary font-medium" : "border-line text-ink-secondary hover:bg-surface-hover"}`}>
            <span className={`grid place-items-center w-5 h-5 rounded-full text-[10px] ${i < step ? "bg-success text-white" : i === step ? "bg-primary text-white" : "bg-surface-hover"}`}>{i < step ? <Check size={11} /> : i + 1}</span>{s}
          </button>
        ))}
      </nav>

      {step === 0 && <TypeStep />}
      {step === 1 && d.design && <EmailEditor design={d.design} onChange={onDesign} meta={meta} />}
      {step === 2 && <ListsStep title="Destinatarios" hint="Elige las listas que recibirán la campaña. Los contactos repetidos se envían una sola vez (deduplicado por correo)." selected={d.listIds} allowAll onChange={(listIds) => patch({ listIds })} excluded={d.excludeListIds} included={d.listIds} />}
      {step === 3 && <ListsStep title="No enviar a" hint="Los contactos de estas listas no recibirán la campaña aunque estén en las listas de destinatarios (tienen prioridad)." selected={d.excludeListIds} onChange={(excludeListIds) => patch({ excludeListIds })} excluded={d.excludeListIds} included={d.listIds} />}
      {step === 4 && <SettingsStep d={d} patch={patch} />}
      {step === 5 && <SendStep id={id} d={d} dirty={dirty} save={doSave} onDone={() => { void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); nav(`/marketing/campanas/${id}`); }} goStep={(n) => void go(n)} />}

      <div className="flex justify-between">
        <Button onClick={() => void go(step - 1)} disabled={step === 0}>Volver</Button>
        {step < STEPS.length - 1 && <Button variant="primary" onClick={() => void go(step + 1)} disabled={save.isPending}>Guardar y continuar</Button>}
      </div>
    </div>
  );
}

function TypeStep(): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Card className="p-5 ring-2 ring-primary">
        <div className="flex items-center gap-3"><Mail className="text-primary" /><div className="font-semibold text-ink">Regular (email)</div><Badge color="blue">Seleccionado</Badge></div>
        <p className="mt-2 text-sm text-ink-secondary">Correo HTML enriquecido diseñado con bloques, enviado a tus listas con seguimiento de aperturas, clics y bajas.</p>
      </Card>
      <Card className="p-5 opacity-70">
        <div className="flex items-center gap-3"><MessageCircle className="text-ink-muted" /><div className="font-semibold text-ink">Mensajería (WhatsApp)</div><Badge>No disponible</Badge></div>
        <p className="mt-2 text-sm text-ink-secondary">Requiere conectar WhatsApp Business API (Meta). Aún no está configurado en Tiger.</p>
      </Card>
    </div>
  );
}

function ListsStep({ title, hint, selected, onChange, allowAll, included, excluded }: {
  title: string; hint: string; selected: string[]; onChange: (ids: string[]) => void; allowAll?: boolean; included: string[]; excluded: string[];
}): JSX.Element {
  const lists = useQuery({ queryKey: ["mkt-audience-lists"], queryFn: () => api.get<{ all: { contacts: number; emails: number }; items: AudienceList[] }>("/mkt/campaigns/audience/lists") });
  const [term, setTerm] = useState("");
  const count = useQuery({
    queryKey: ["mkt-audience-count", included, excluded],
    queryFn: () => api.post<{ count: number }>("/mkt/campaigns/audience/count", { listIds: included, excludeListIds: excluded }),
    enabled: included.length > 0,
  });
  const toggle = (id: string): void => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const items = (lists.data?.items ?? []).filter((l) => !term || l.name.toLowerCase().includes(term.toLowerCase()) || (l.description ?? "").toLowerCase().includes(term.toLowerCase()));
  // Una lista no puede estar a la vez en destinatarios y exclusión.
  const blocked = allowAll ? excluded : included;
  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-line">
        <div>
          <div className="sec-title">{title}</div>
          <p className="text-xs text-ink-muted max-w-xl">{hint}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <Users size={16} className="text-primary" />
          {included.length === 0 ? <span className="text-ink-muted">Sin destinatarios</span>
            : count.isLoading ? <span className="text-ink-muted">Calculando…</span>
            : <span className="text-ink"><b className="tnum">{(count.data?.count ?? 0).toLocaleString("es-CO")}</b> destinatarios finales</span>}
        </div>
      </div>
      <div className="px-5 py-3 border-b border-line">
        <div className="relative max-w-sm"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" /><Input className="pl-8" placeholder="Buscar lista…" value={term} onChange={(e) => setTerm(e.target.value)} /></div>
      </div>
      {lists.isLoading ? <Loading /> : lists.isError ? <ErrorState message="No se pudieron cargar las listas." onRetry={() => void lists.refetch()} /> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className="th w-10" /><th className="th">Nombre</th><th className="th">Tipo</th><th className="th hidden md:table-cell">Descripción</th><th className="th text-right">Contactos</th><th className="th text-right">Con email</th></tr></thead>
            <tbody>
              {allowAll && lists.data && (
                <tr className="hover:bg-surface-hover cursor-pointer" onClick={() => toggle("all")}>
                  <td className="td"><input type="checkbox" readOnly checked={selected.includes("all")} aria-label="Todos los contactos" /></td>
                  <td className="td font-medium text-ink">Todos los contactos</td><td className="td"><Badge color="blue">Especial</Badge></td>
                  <td className="td hidden md:table-cell">Toda la base de contactos de la organización</td>
                  <td className="td text-right tnum">{lists.data.all.contacts.toLocaleString("es-CO")}</td><td className="td text-right tnum">{lists.data.all.emails.toLocaleString("es-CO")}</td>
                </tr>
              )}
              {items.map((l) => {
                const dis = blocked.includes(l.id);
                return (
                  <tr key={l.id} className={dis ? "opacity-50" : "hover:bg-surface-hover cursor-pointer"} onClick={() => { if (!dis) toggle(l.id); }}>
                    <td className="td"><input type="checkbox" readOnly disabled={dis} checked={selected.includes(l.id)} aria-label={l.name} /></td>
                    <td className="td font-medium text-ink">{l.name}{dis && <span className="ml-2 text-[11px] text-ink-muted">({allowAll ? "en exclusión" : "en destinatarios"})</span>}</td>
                    <td className="td">{l.kind === "dynamic" ? "Dinámica" : "Estática"}{l.source === "clientify" && <span className="ml-1 text-[11px] text-ink-muted">· Clientify</span>}</td>
                    <td className="td hidden md:table-cell max-w-xs truncate">{l.description ?? "—"}</td>
                    <td className="td text-right tnum">{l.contacts.toLocaleString("es-CO")}</td><td className="td text-right tnum">{l.emails.toLocaleString("es-CO")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!items.length && <EmptyState title="No hay listas" body="Crea listas en Marketing › Contactos para segmentar tus envíos." />}
        </div>
      )}
    </Card>
  );
}

function SettingsStep({ d, patch }: { d: Draft; patch: (p: Partial<Draft>) => void }): JSX.Element {
  const subjRef = useRef<HTMLInputElement>(null);
  const insertVar = (v: string): void => {
    const el = subjRef.current; const pos = el?.selectionStart ?? d.subject.length;
    patch({ subject: (d.subject.slice(0, pos) + v + d.subject.slice(pos)).slice(0, 250) });
  };
  return (
    <Card className="p-5 space-y-4 max-w-3xl">
      <div className="sec-title">Configuración inicial</div>
      <Field label="Nombre de la campaña (interno)" required><Input value={d.name} maxLength={160} onChange={(e) => patch({ name: e.target.value })} /></Field>
      <Field label="Asunto" required hint={`${d.subject.length}/250 · Recomendado: menos de 60 caracteres.`}>
        <input ref={subjRef} className="input" value={d.subject} maxLength={250} onChange={(e) => patch({ subject: e.target.value })} placeholder="Ej.: {{nombre}}, tenemos novedades para ti" />
      </Field>
      <div className="flex flex-wrap gap-1.5 -mt-2">
        {VARIABLES.filter((v) => v.key !== "{{baja_url}}").map((v) => <button key={v.key} type="button" className="badge bg-surface-hover text-ink-secondary hover:text-primary" onClick={() => insertVar(v.key)}>+ {v.label}</button>)}
      </div>
      <Field label="Preencabezado" hint="Texto que muestran los clientes de correo junto al asunto."><Input value={d.preheader} maxLength={250} onChange={(e) => patch({ preheader: e.target.value })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre del remitente"><Input value={d.fromName} maxLength={120} onChange={(e) => patch({ fromName: e.target.value })} /></Field>
        <Field label="Responder a" hint="Opcional." error={d.replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.replyTo) ? "Correo no válido" : undefined}>
          <Input type="email" value={d.replyTo} maxLength={200} onChange={(e) => patch({ replyTo: e.target.value })} />
        </Field>
      </div>
      <Field label="Carpeta" hint="Opcional, para organizar la lista de campañas."><Input value={d.folder} maxLength={80} onChange={(e) => patch({ folder: e.target.value })} /></Field>
      <p className="text-xs text-ink-muted">El correo se envía desde el buzón configurado en Configuración › Correo (Microsoft 365/SMTP).</p>
    </Card>
  );
}

function SendStep({ id, d, dirty, save, onDone, goStep }: { id: string; d: Draft; dirty: boolean; save: () => Promise<boolean>; onDone: () => void; goStep: (n: number) => void }): JSX.Element {
  const me = useMe();
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [when, setWhen] = useState(() => {
    const t = new Date(Date.now() + 3600_000); t.setMinutes(0, 0, 0);
    return new Date(t.getTime() - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [testEmail, setTestEmail] = useState(me.data?.email ?? "");
  const count = useQuery({
    queryKey: ["mkt-audience-count", d.listIds, d.excludeListIds],
    queryFn: () => api.post<{ count: number }>("/mkt/campaigns/audience/count", { listIds: d.listIds, excludeListIds: d.excludeListIds }),
    enabled: d.listIds.length > 0,
  });
  const audit = useQuery({
    queryKey: ["mkt-campaign-audit", id, d.design, d.subject, d.preheader, d.fromName],
    queryFn: () => api.post<{ audit: AuditIssue[] }>("/mkt/campaigns/render", { design: d.design, subject: d.subject, preheader: d.preheader, fromName: d.fromName }),
    enabled: Boolean(d.design),
  });
  const errors = (audit.data?.audit ?? []).filter((i) => i.level === "error");
  const warnings = (audit.data?.audit ?? []).filter((i) => i.level === "warning");
  const test = useMutation({
    mutationFn: async () => { if (dirty && !(await save())) throw new Error("No se pudo guardar."); return api.post(`/mkt/campaigns/${id}/test`, { email: testEmail.trim() }); },
    onSuccess: () => toast.success(`Correo de prueba enviado a ${testEmail}.`),
    onError: (e) => toast.error(e.message),
  });
  const send = useMutation({
    mutationFn: async () => {
      if (dirty && !(await save())) throw new Error("No se pudo guardar.");
      return api.post<{ status: string; recipients: number }>(`/mkt/campaigns/${id}/send`, mode === "now" ? { mode } : { mode, scheduledAt: new Date(when).toISOString() });
    },
    onSuccess: (r) => { toast.success(r.status === "scheduled" ? "Campaña programada." : `Envío iniciado a ${r.recipients.toLocaleString("es-CO")} destinatarios.`); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  const n = count.data?.count ?? 0;
  const canSend = d.listIds.length > 0 && n > 0 && errors.length === 0 && !audit.isLoading;
  // Ritmo de Microsoft 365 (~25/min por defecto): estimación de duración.
  const minutes = Math.ceil(n / 25);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5 space-y-3">
        <div className="sec-title">Resumen</div>
        <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
          <dt className="text-ink-muted">Asunto</dt><dd className="text-ink">{d.subject || <span className="text-danger">Falta el asunto</span>} <button className="link text-xs ml-1" onClick={() => goStep(4)}>editar</button></dd>
          <dt className="text-ink-muted">Remitente</dt><dd className="text-ink">{d.fromName || "—"}</dd>
          <dt className="text-ink-muted">Destinatarios</dt>
          <dd className="text-ink">{d.listIds.length === 0 ? <span className="text-danger">Sin listas</span> : count.isLoading ? "Calculando…" : <b className="tnum">{n.toLocaleString("es-CO")}</b>} <button className="link text-xs ml-1" onClick={() => goStep(2)}>editar</button></dd>
          <dt className="text-ink-muted">Duración estimada</dt><dd className="text-ink">{n ? (minutes < 60 ? `~${minutes} min` : `~${(minutes / 60).toFixed(1)} h`) : "—"} <span className="text-xs text-ink-muted">(límite de envío de Microsoft 365)</span></dd>
        </dl>
        {audit.isLoading ? <p className="text-xs text-ink-muted">Revisando el correo…</p> : (
          <div className="space-y-1.5">
            {errors.map((e, i) => <p key={i} className="flex gap-1.5 text-xs text-danger"><AlertCircle size={14} className="shrink-0" />{e.message}</p>)}
            {warnings.map((e, i) => <p key={i} className="flex gap-1.5 text-xs text-warning"><AlertCircle size={14} className="shrink-0" />{e.message}</p>)}
            {!errors.length && !warnings.length && <p className="text-xs text-success">Sin problemas detectados en la auditoría.</p>}
            {(errors.length > 0 || warnings.length > 0) && <button className="link text-xs" onClick={() => goStep(1)}>Ir al diseño</button>}
          </div>
        )}
        <div className="pt-3 border-t border-line">
          <Field label="Enviar prueba a">
            <div className="flex gap-2">
              <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="correo@empresa.com" />
              <Button onClick={() => test.mutate()} disabled={test.isPending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)}>{test.isPending ? "Enviando…" : "Enviar prueba"}</Button>
            </div>
          </Field>
          <p className="mt-1 text-xs text-ink-muted">La prueba usa tus datos como variables y no registra estadísticas.</p>
        </div>
      </Card>
      <Card className="p-5 space-y-4">
        <div className="sec-title">Envío</div>
        <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="radio" className="mt-1" checked={mode === "now"} onChange={() => setMode("now")} /><span><b className="text-ink">Enviar ahora</b><br /><span className="text-ink-muted text-xs">Se encola y se envía de forma escalonada.</span></span></label>
        <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="radio" className="mt-1" checked={mode === "schedule"} onChange={() => setMode("schedule")} /><span><b className="text-ink">Programar</b><br /><span className="text-ink-muted text-xs">Se lanzará automáticamente en la fecha indicada (hora local).</span></span></label>
        {mode === "schedule" && <Field label="Fecha y hora de envío"><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>}
        <Button variant="primary" className="w-full" disabled={!canSend || send.isPending}
          onClick={() => { if (confirm(mode === "now" ? `¿Enviar la campaña ahora a ${n.toLocaleString("es-CO")} destinatarios?` : `¿Programar la campaña para el ${new Date(when).toLocaleString("es-CO")}?`)) send.mutate(); }}>
          <Send size={15} className="mr-1.5 inline" />{send.isPending ? "Procesando…" : mode === "now" ? "Enviar campaña" : "Programar campaña"}
        </Button>
        {!canSend && !audit.isLoading && <p className="text-xs text-ink-muted">Para enviar necesitas destinatarios con correo válido y ningún error en la auditoría.</p>}
      </Card>
    </div>
  );
}
